import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, ChevronLeft, ChevronRight, Calendar, Receipt } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesList() {
  const { getAuthHeaders, currentBranch } = useAuth();
  const [sales, setSales] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => { fetchSales(); }, [page, dateFilter, dateFrom, dateTo, currentBranch]);

  const getDateRange = () => {
    const now = new Date();
    const todayStr = now.toISOString().split('T')[0];
    if (dateFilter === 'today') return { from: todayStr, to: '' };
    if (dateFilter === 'week') {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return { from: weekAgo.toISOString().split('T')[0], to: '' };
    }
    if (dateFilter === 'month') {
      const monthAgo = new Date(now);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return { from: monthAgo.toISOString().split('T')[0], to: '' };
    }
    if (dateFilter === 'custom') return { from: dateFrom, to: dateTo };
    return { from: '', to: '' };
  };

  const fetchSales = async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', '30');
      if (search) params.append('search', search);
      if (currentBranch && !search) params.append('branch', currentBranch);
      const range = getDateRange();
      if (range.from) params.append('from', range.from);
      if (range.to) params.append('to', range.to);
      const res = await axios.get(`${API}/api/pos/sales?${params}`, { headers: getAuthHeaders() });
      setSales(res.data.sales || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    } catch (err) { console.error(err); }
  };

  const handleSearch = () => { setPage(1); fetchSales(); };

  return (
    <div data-testid="sales-history-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1D3C6E] font-['Outfit']">Historial de Ventas</h1>
        <span className="text-sm text-slate-500">{total} ventas</span>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-xs font-medium text-slate-600 mb-1 block">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input data-testid="sales-search" className="pl-10" placeholder="Ticket o nombre de cliente..."
                value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} />
            </div>
          </div>
          {/* Date filter buttons */}
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">Periodo</label>
            <div className="flex gap-1">
              {[
                { key: 'all', label: 'Todos' },
                { key: 'today', label: 'Hoy' },
                { key: 'week', label: 'Semana' },
                { key: 'month', label: 'Mes' },
                { key: 'custom', label: 'Rango' },
              ].map(f => (
                <button key={f.key} onClick={() => { setDateFilter(f.key); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${dateFilter === f.key ? 'bg-[#1D3C6E] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>
          {/* Custom date range */}
          {dateFilter === 'custom' && (
            <div className="flex items-center gap-2">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Desde</label>
                <Input type="date" className="h-9 text-sm w-36" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Hasta</label>
                <Input type="date" className="h-9 text-sm w-36" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Fecha</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Ticket</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Cliente</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Total</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Metodo</th>
              <th className="text-left px-4 py-3 font-medium text-slate-600">Tipo</th>
            </tr>
          </thead>
          <tbody>
            {sales.map((sale) => {
              const to = `/sales/${sale.id}`;
              const cellCls = 'block px-4 py-3 -mx-4 -my-3 text-inherit no-underline';
              return (
                <tr key={sale.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-500"><Link to={to} className={cellCls}>{new Date(sale.created_at).toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Link></td>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-[#1D3C6E]"><Link to={to} className={cellCls}>{sale.ticket || sale.id?.slice(-8)}</Link></td>
                  <td className="px-4 py-3"><Link to={to} className={cellCls}>{sale.client_name || 'Venta general'}</Link></td>
                  <td className="px-4 py-3 font-medium"><Link to={to} className={cellCls}>${(sale.total || 0).toFixed(2)}</Link></td>
                  <td className="px-4 py-3 text-slate-600"><Link to={to} className={cellCls}>{sale.payment_method}</Link></td>
                  <td className="px-4 py-3">
                    <Link to={to} className={cellCls}>
                      <span className={`status-badge ${sale.sale_type === 'repair' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {sale.sale_type === 'repair' ? 'Reparacion' : 'POS'}
                      </span>
                    </Link>
                  </td>
                </tr>
              );
            })}
            {sales.length === 0 && (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">
                <Receipt className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p>No se encontraron ventas</p>
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-xs text-slate-500">Pagina {page} de {pages} ({total} ventas)</p>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-8 px-3">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => setPage(page + 1)} className="h-8 px-3">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
